import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';
import { Rocket, Shield, Cpu, Globe, ArrowRight, Github, Mail } from 'lucide-react';

import Navbar from '../components/Navbar';

export default function UseCases() {
  const cases = [
    {
      title: "Venture Pre-Seed",
      tag: "Founders",
      description: "Go from napkin sketch to a functional, investor-ready prototype in 48 hours. Not a pitch deck—a product.",
      icon: Rocket
    },
    {
      title: "Enterprise Internal",
      tag: "Operations",
      description: "Synthesize high-security internal dashboards and tools that integrate with existing legacy data silos.",
      icon: Shield
    },
    {
      title: "AI Native Startups",
      tag: "Next-Gen",
      description: "Build applications where AI isn't just a feature, but the core architecture. Leverage native LLM integration.",
      icon: Cpu
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="pt-48 pb-24 px-8">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-6xl md:text-8xl font-mono uppercase tracking-tighter mb-8"
            >
              Built for <span className="text-[#0074D9]">Speed.</span>
            </motion.h1>
            <p className="text-zinc-500 text-xl font-light max-w-2xl mx-auto leading-relaxed">
              Whether you're a solo founder or an enterprise architect, Yacht Labs provides the leverage you need to ship.
            </p>
          </div>
        </section>

        <section className="py-32 px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {cases.map((useCase, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-10 border border-zinc-900 bg-zinc-950/50 rounded-[32px] group hover:border-[#0074D9]/30 transition-all"
              >
                <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center text-[#0074D9] mb-8 group-hover:scale-110 transition-transform">
                  <useCase.icon size={24} />
                </div>
                <h2 className="tech-label text-[#0074D9] mb-4">{useCase.tag}</h2>
                <h3 className="text-2xl font-mono uppercase tracking-tight mb-4">{useCase.title}</h3>
                <p className="text-zinc-500 font-light leading-relaxed mb-8">{useCase.description}</p>
                <Link to="/studio" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white group-hover:gap-4 transition-all">
                  Start Building <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-32 px-8 bg-zinc-950 border-t border-white/5 overflow-hidden">
          <div className="max-w-6xl mx-auto">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
                <div className="space-y-8">
                  <h2 className="tech-label">The Founder Flow</h2>
                  <h3 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter leading-tight">Ship your MVP before <br/> self-doubt kicks in.</h3>
                  <p className="text-zinc-400 text-lg font-light leading-relaxed">
                    Yacht Labs removes the "technical wall." You focus on the problem and the customer, we handle the synthesis. No more waiting 3 months for a V1.
                  </p>
                  <blockquote className="pl-6 border-l-2 border-[#0074D9] italic text-zinc-500">
                    "Yacht Labs literally shortened our prototyping phase by 12 weeks. We went into our Seed round with a functional app that we built in a weekend."
                  </blockquote>
                </div>
                <div className="relative">
                   <div className="absolute inset-0 bg-[#0074D9]/10 blur-[120px]" />
                   <div className="relative aspect-video bg-black border border-zinc-800 rounded-3xl p-8 overflow-hidden">
                      <div className="flex items-center gap-2 mb-8">
                        <div className="w-3 h-3 rounded-full bg-zinc-800" />
                        <div className="w-3 h-3 rounded-full bg-zinc-800" />
                        <div className="w-3 h-3 rounded-full bg-zinc-800" />
                      </div>
                      <div className="space-y-4">
                        <div className="h-2 w-3/4 bg-zinc-900 rounded-full" />
                        <div className="h-2 w-full bg-zinc-900 rounded-full" />
                        <div className="h-2 w-1/2 bg-[#0074D9]/40 rounded-full" />
                        <div className="pt-8 grid grid-cols-2 gap-4">
                           <div className="h-32 bg-zinc-900 rounded-xl" />
                           <div className="h-32 bg-zinc-900 rounded-xl" />
                        </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </section>
      </main>

      <footer className="p-24 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-24">
          <div className="col-span-2 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <YachtLogo className="w-8 h-8" />
              <span className="font-mono text-2xl uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-zinc-500 font-light leading-relaxed max-w-sm">
              Synthesizing the future of digital architecture. Empowering founders to bridge the gap between intent and existence.
            </p>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Platform</h4>
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Synthesis</Link>
              <Link to="/use-cases" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Use Cases</Link>
              <Link to="/solutions" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Solutions</Link>
              <Link to="/pricing" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
            </div>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Connect</h4>
            <div className="flex flex-col gap-4">
              <a href="#" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Github size={14} /> GitHub
              </a>
              <a href="mailto:hello@yacht-labs.com" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Mail size={14} /> Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
